import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";
import {
    FaBell,
    FaChevronRight,
    FaUsersCog,
    FaTasks,
    FaUmbrellaBeach,
    FaTools,
    FaCheckSquare,
    FaClipboardList,
    FaShieldAlt,
    FaLock,
    FaExchangeAlt,
    FaUserEdit,
    FaCommentDots,
    FaPaperPlane,
    FaGavel,
    FaPlusCircle,
    FaSyncAlt,
    FaCheckCircle,
    FaCircleNotch,
} from "react-icons/fa";

const T = {
    bg: "#f4f6fb",
    white: "#ffffff",
    border: "#e2e8f0",
    borderSoft: "#edf1f7",
    shadow: "0 1px 3px rgba(15,23,42,0.06), 0 0 0 1px rgba(15,23,42,0.04)",
    shadowMd: "0 10px 26px rgba(15,23,42,0.10)",
    radius: 16,
    text1: "#0f172a",
    text2: "#475569",
    text3: "#94a3b8",
    primary: "#6366f1",
    primaryDark: "#4f46e5",
};

const CATEGORIES = [
    {
        key: "taskNotifications",
        name: "Task Notifications",
        short: "Task",
        desc: "Emails sent when a task's status, owner, or discussion changes.",
        icon: FaTasks,
        accent: "#6366f1",
        children: [
            { key: "taskStatusChange", name: "Status Change", desc: "Task marked as In Progress, Done, etc.", icon: FaExchangeAlt },
            { key: "taskAssigneeChange", name: "Assignee Change", desc: "Task reassigned to another employee", icon: FaUserEdit },
            { key: "taskComment", name: "Comments", desc: "New comment or reply posted on a task", icon: FaCommentDots },
        ],
    },
    {
        key: "leaveNotifications",
        name: "Leave Notifications",
        short: "Leave",
        desc: "Everything tied to leave requests, balances, and decisions.",
        icon: FaUmbrellaBeach,
        accent: "#10b981",
        children: [
            { key: "leaveApplied", name: "Applied / Submitted", desc: "New or updated leave request sent for review", icon: FaPaperPlane },
            { key: "leaveDecision", name: "Approved / Rejected / Forwarded / Cancelled", desc: "Any decision or status change on a leave request", icon: FaGavel },
            { key: "leaveCredited", name: "Credited", desc: "Monthly or manual leave credit, plan assignment", icon: FaPlusCircle },
        ],
    },
    {
        key: "itSupportNotifications",
        name: "IT Support Notifications",
        short: "IT Support",
        desc: "Emails for IT tickets raised by employees and their resolution.",
        icon: FaTools,
        accent: "#0ea5e9",
        children: [
            { key: "itRequestSubmitted", name: "Request Submitted", desc: "New IT request raised — sent to admins", icon: FaPaperPlane },
            { key: "itRequestStatus", name: "Status Update", desc: "Request accepted, resolved, or rejected", icon: FaSyncAlt },
        ],
    },
    {
        key: "checklistNotifications",
        name: "Checklist Reminders",
        short: "Checklist",
        desc: "Due-soon reminders for personal checklist items.",
        icon: FaCheckSquare,
        accent: "#f59e0b",
        children: null,
    },
    {
        key: "evaluationNotifications",
        name: "Evaluation Reminders",
        short: "Evaluation",
        desc: "Weekly nudges to team leaders for pending evaluations.",
        icon: FaClipboardList,
        accent: "#8b5cf6",
        children: null,
    },
];

const SECURITY_KEY = "__security__";
const LOCKED_ITEMS = [
    { name: "Account Credentials & Password Reset", desc: "Sent on user creation and password reset — required to log in" },
    { name: "Asset Allocation OTP", desc: "Security code required to confirm an asset handover" },
];

const NAV_ITEMS = [
    ...CATEGORIES,
    { key: SECURITY_KEY, name: "Security & Account", short: "Security", icon: FaShieldAlt, accent: "#64748b", locked: true },
];

// Purely presentational grouping for the sidebar — doesn't touch the data
// model, just clusters the same NAV_ITEMS keys under section labels.
const NAV_GROUPS = [
    { label: "Operational", keys: ["taskNotifications", "leaveNotifications", "itSupportNotifications"] },
    { label: "Reminders", keys: ["checklistNotifications", "evaluationNotifications"] },
    { label: "Account", keys: [SECURITY_KEY] },
];

const ToggleSwitch = ({ checked, onChange, disabled, size = "md", outlined = false }) => {
    const w = size === "sm" ? 38 : 46;
    const h = size === "sm" ? 21 : 25;
    const knob = size === "sm" ? 15 : 19;
    const gap = 3;
    return (
        <label
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", width: w, height: h, flexShrink: 0, cursor: disabled ? "not-allowed" : "pointer" }}
        >
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span
                style={{
                    position: "absolute",
                    inset: 0,
                    background: disabled ? "#e2e8f0" : checked ? T.primary : "#cbd5e1",
                    borderRadius: 20,
                    transition: "background 0.18s, box-shadow 0.18s",
                    // Ring keeps the pill's edge visible even when the track color
                    // nearly matches the surface it's sitting on (e.g. the indigo hero banner)
                    boxShadow: outlined ? "0 0 0 1.5px rgba(255,255,255,0.6)" : "none",
                }}
            >
                <span
                    style={{
                        position: "absolute",
                        height: knob,
                        width: knob,
                        left: gap,
                        top: gap,
                        background: "#fff",
                        borderRadius: "50%",
                        transition: "transform 0.18s",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                        transform: checked ? `translateX(${w - knob - gap * 2}px)` : "translateX(0)",
                    }}
                />
            </span>
        </label>
    );
};

const NotificationSettingsPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [syncStatus, setSyncStatus] = useState("idle"); // idle | saving | saved | error
    const [settings, setSettings] = useState(null);
    const [activeKey, setActiveKey] = useState(CATEGORIES[0].key);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        if (!token) {
            toast.error("You must be logged in to access this page.");
            navigate("/");
            return;
        }
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    useEffect(() => {
        if (syncStatus === "saved") {
            const t = setTimeout(() => setSyncStatus("idle"), 1600);
            return () => clearTimeout(t);
        }
    }, [syncStatus]);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/notification-settings`, { headers });
            setSettings(res.data);
        } catch (e) {
            toast.error("Failed to load notification settings");
        } finally {
            setLoading(false);
        }
    };

    // Optimistically applies the change, then persists it — reverts + toasts on failure
    const persist = async (next, previous) => {
        setSyncStatus("saving");
        try {
            const res = await axios.put(`${API_BASE_URL}/api/notification-settings`, next, { headers });
            setSettings(res.data);
            setSyncStatus("saved");
        } catch (e) {
            setSettings(previous);
            setSyncStatus("error");
            toast.error("Failed to save — change reverted");
        }
    };

    const handleToggle = (key) => {
        setSettings((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            persist(next, prev);
            return next;
        });
    };

    const handleBulkToggle = (children, value) => {
        setSettings((prev) => {
            const next = { ...prev };
            children.forEach((c) => { next[c.key] = value; });
            persist(next, prev);
            return next;
        });
    };

    const totalOn = useMemo(() => {
        if (!settings) return 0;
        return CATEGORIES.reduce((sum, c) => sum + (c.children ? c.children.filter((ch) => settings[ch.key]).length : (settings[c.key] ? 1 : 0)), 0);
    }, [settings]);
    const totalAll = useMemo(() => CATEGORIES.reduce((sum, c) => sum + (c.children ? c.children.length : 1), 0), []);

    if (loading || !settings) {
        return (
            <>
                <div className="fixed-top">
                    <AdminNavbar />
                </div>
                <div style={{ paddingTop: 108, height: "100vh", boxSizing: "border-box", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div className="ns-spinner" />
                </div>
                <style>{`
                    .ns-spinner {
                        width: 36px; height: 36px;
                        border: 3px solid ${T.border};
                        border-top-color: ${T.primary};
                        border-radius: 50%;
                        animation: ns-spin 0.7s linear infinite;
                    }
                    @keyframes ns-spin { to { transform: rotate(360deg); } }
                `}</style>
            </>
        );
    }

    const activeCat = NAV_ITEMS.find((c) => c.key === activeKey);
    const isSecurity = activeKey === SECURITY_KEY;

    return (
        <>
            <div className="fixed-top">
                <AdminNavbar />
            </div>

            <div style={{ paddingTop: 108, height: "100vh", boxSizing: "border-box", background: T.bg, display: "flex", flexDirection: "column", overflow: "hidden" }}>

                {/* ── Breadcrumb strip ── */}
                <div style={{
                    background: T.white,
                    borderBottom: `1px solid ${T.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 24px",
                    height: 44,
                    flexShrink: 0,
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{
                            width: 28, height: 28, borderRadius: 7,
                            background: "#eef2ff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <FaUsersCog size={13} color={T.primary} />
                        </div>
                        <span style={{ fontSize: "0.85rem", fontWeight: 700, color: T.text1 }}>
                            Setup Management
                        </span>
                        <FaChevronRight size={10} color={T.text3} style={{ margin: "0 2px" }} />
                        <span style={{ fontSize: "0.78rem", color: T.text2 }}>Notification Settings</span>
                    </div>

                    <span style={{
                        fontSize: "0.68rem", fontWeight: 800, color: T.text3,
                        textTransform: "uppercase", letterSpacing: "0.1em",
                    }}>
                        Administration
                    </span>
                </div>

                {/* ── Main content (only region that scrolls) ── */}
                <div className="ns-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "28px 24px 40px" }}>
                    <div className="ns-shell" style={{ width: "100%", display: "flex", flexDirection: "column", minHeight: "100%" }}>

                        {/* Sidebar + right column (hero above detail panel) */}
                        <div className="ns-layout" style={{ display: "flex", gap: 20, alignItems: "stretch", flex: 1, minHeight: 0 }}>

                            {/* Sidebar nav — grouped into labeled sections, flush to the top, stretched to full height */}
                            <div className="ns-sidebar" style={{
                                width: 264,
                                flexShrink: 0,
                                background: T.white,
                                borderRadius: T.radius,
                                boxShadow: T.shadow,
                                padding: 10,
                                overflowY: "auto",
                            }}>
                                {NAV_GROUPS.map((group, gi) => (
                                    <div key={group.label} style={{ marginTop: gi === 0 ? 0 : 14 }}>
                                        <div style={{
                                            fontSize: "0.66rem",
                                            fontWeight: 800,
                                            color: T.text3,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.07em",
                                            padding: "6px 12px 6px",
                                        }}>
                                            {group.label}
                                        </div>

                                        {group.keys.map((key) => {
                                            const item = NAV_ITEMS.find((n) => n.key === key);
                                            const Icon = item.icon;
                                            const active = item.key === activeKey;
                                            const onCount = item.children
                                                ? item.children.filter((c) => settings[c.key]).length
                                                : item.locked ? LOCKED_ITEMS.length : (settings[item.key] ? 1 : 0);
                                            const total = item.children ? item.children.length : (item.locked ? LOCKED_ITEMS.length : 1);
                                            const dimmed = !item.locked && !settings.masterEnabled;

                                            return (
                                                <button
                                                    key={item.key}
                                                    onClick={() => setActiveKey(item.key)}
                                                    className="ns-nav-item"
                                                    style={{
                                                        width: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 12,
                                                        padding: "11px 12px",
                                                        marginBottom: 2,
                                                        border: "none",
                                                        borderRadius: 10,
                                                        cursor: "pointer",
                                                        textAlign: "left",
                                                        background: active ? item.accent + "14" : "transparent",
                                                        borderLeft: `3px solid ${active ? item.accent : "transparent"}`,
                                                        opacity: dimmed ? 0.55 : 1,
                                                    }}
                                                >
                                                    <div style={{
                                                        width: 30, height: 30, borderRadius: 8,
                                                        background: active ? item.accent + "22" : T.bg,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        flexShrink: 0,
                                                    }}>
                                                        <Icon size={13} color={item.accent} />
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{
                                                            fontSize: "0.83rem", fontWeight: active ? 800 : 600,
                                                            color: active ? T.text1 : T.text2,
                                                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                                        }}>
                                                            {item.short}
                                                        </div>
                                                    </div>
                                                    {item.locked ? (
                                                        <FaLock size={11} color={T.text3} />
                                                    ) : (
                                                        <span style={{
                                                            fontSize: "0.66rem", fontWeight: 700,
                                                            color: onCount === total ? item.accent : T.text3,
                                                            background: T.bg, borderRadius: 20, padding: "2px 7px",
                                                            flexShrink: 0,
                                                        }}>
                                                            {onCount}/{total}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>

                            {/* Right column: hero banner above the detail panel */}
                            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 20, minHeight: 0 }}>

                                {/* Hero banner / master switch — scoped to this column only */}
                                <div
                                    className="ns-hero"
                                    style={{
                                        borderRadius: T.radius,
                                        padding: "24px 28px",
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 20,
                                        color: "#fff",
                                        background: "linear-gradient(120deg, #1e1b4b 0%, #3730a3 55%, #4f46e5 100%)",
                                        boxShadow: "0 14px 30px rgba(30,27,75,0.35)",
                                        position: "relative",
                                        overflow: "hidden",
                                    }}
                                >
                                    <div className="ns-hero-glow" />
                                    <div style={{ display: "flex", alignItems: "center", gap: 16, position: "relative", zIndex: 1 }}>
                                        <div style={{
                                            width: 46, height: 46, borderRadius: 12,
                                            background: "rgba(255,255,255,0.18)",
                                            backdropFilter: "blur(4px)",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            flexShrink: 0,
                                        }}>
                                            <FaBell size={19} color="#fff" />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: "1.1rem", fontWeight: 800 }}>
                                                Email Notifications
                                            </div>
                                            <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.85)", marginTop: 3, maxWidth: 460 }}>
                                                Master switch for every automated email this system sends — {totalOn} of {totalAll} event types currently on. Every toggle saves instantly.
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 16, position: "relative", zIndex: 1 }}>
                                        <span className="ns-sync-indicator" style={{
                                            fontSize: "0.72rem", fontWeight: 700, color: "rgba(255,255,255,0.9)",
                                            display: "flex", alignItems: "center", gap: 6,
                                            minWidth: 84, justifyContent: "flex-end",
                                        }}>
                                            {syncStatus === "saving" && <><FaCircleNotch className="ns-spin-icon" size={11} /> Saving…</>}
                                            {syncStatus === "saved" && <><FaCheckCircle size={11} /> Saved</>}
                                            {syncStatus === "idle" && (settings.masterEnabled ? "All On" : "All Off")}
                                        </span>
                                        <ToggleSwitch
                                            checked={settings.masterEnabled}
                                            onChange={() => handleToggle("masterEnabled")}
                                            outlined
                                        />
                                    </div>
                                </div>

                            {/* Detail panel */}
                            <div className="ns-panel" style={{
                                flex: 1,
                                minWidth: 0,
                                background: T.white,
                                borderRadius: T.radius,
                                boxShadow: T.shadow,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0,
                            }}>
                                {/* Panel header */}
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 16,
                                    padding: "20px 24px",
                                    borderBottom: `1px solid ${T.border}`,
                                    flexShrink: 0,
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                        <div style={{
                                            width: 42, height: 42, borderRadius: 11,
                                            background: activeCat.accent + "18",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            flexShrink: 0,
                                        }}>
                                            <activeCat.icon size={17} color={activeCat.accent} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: "1rem", fontWeight: 800, color: T.text1 }}>
                                                {activeCat.name}
                                            </div>
                                            <div style={{ fontSize: "0.78rem", color: T.text3, marginTop: 2 }}>
                                                {isSecurity
                                                    ? "Transactional emails that can't be turned off — required for account security."
                                                    : activeCat.desc}
                                            </div>
                                        </div>
                                    </div>
                                    {!isSecurity && (
                                        <ToggleSwitch checked={settings[activeCat.key]} onChange={() => handleToggle(activeCat.key)} />
                                    )}
                                </div>

                                {/* Panel body */}
                                <div style={{ padding: "8px 24px 20px", flex: 1, overflowY: "auto" }}>
                                    {isSecurity && LOCKED_ITEMS.map((item, idx) => (
                                        <div
                                            key={item.name}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 14,
                                                padding: "16px 4px",
                                                borderBottom: idx < LOCKED_ITEMS.length - 1 ? `1px solid ${T.borderSoft}` : "none",
                                            }}
                                        >
                                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                <div style={{
                                                    width: 30, height: 30, borderRadius: 8,
                                                    background: T.bg,
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    flexShrink: 0,
                                                }}>
                                                    <FaLock size={12} color={T.text3} />
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: "0.85rem", fontWeight: 700, color: T.text2 }}>{item.name}</div>
                                                    <div style={{ fontSize: "0.74rem", color: T.text3, marginTop: 1 }}>{item.desc}</div>
                                                </div>
                                            </div>
                                            <ToggleSwitch checked={true} disabled={true} onChange={() => {}} />
                                        </div>
                                    ))}

                                    {!isSecurity && !activeCat.children && (
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 12,
                                            padding: "18px 16px",
                                            marginTop: 8,
                                            background: T.bg,
                                            borderRadius: 12,
                                        }}>
                                            <FaCheckCircle size={16} color={activeCat.accent} />
                                            <span style={{ fontSize: "0.83rem", color: T.text2 }}>
                                                This category currently sends a single type of email. Use the switch above to turn it on or off.
                                            </span>
                                        </div>
                                    )}

                                    {!isSecurity && activeCat.children && (
                                        <>
                                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 14, padding: "10px 4px 4px" }}>
                                                <button
                                                    onClick={() => handleBulkToggle(activeCat.children, true)}
                                                    className="ns-link-btn"
                                                    style={{ color: activeCat.accent }}
                                                >
                                                    Enable all
                                                </button>
                                                <span style={{ color: T.border }}>|</span>
                                                <button
                                                    onClick={() => handleBulkToggle(activeCat.children, false)}
                                                    className="ns-link-btn"
                                                    style={{ color: T.text3 }}
                                                >
                                                    Disable all
                                                </button>
                                            </div>

                                            {activeCat.children.map((child, ci) => {
                                                const ChildIcon = child.icon;
                                                const childDisabled = !settings.masterEnabled || !settings[activeCat.key];
                                                return (
                                                    <div
                                                        key={child.key}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            gap: 14,
                                                            padding: "14px 4px",
                                                            borderBottom: ci < activeCat.children.length - 1 ? `1px solid ${T.borderSoft}` : "none",
                                                            opacity: childDisabled ? 0.5 : 1,
                                                        }}
                                                    >
                                                        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                                                            <div style={{
                                                                width: 32, height: 32, borderRadius: 9,
                                                                background: T.bg,
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                flexShrink: 0,
                                                            }}>
                                                                <ChildIcon size={13} color={activeCat.accent} />
                                                            </div>
                                                            <div style={{ minWidth: 0 }}>
                                                                <div style={{ fontSize: "0.86rem", fontWeight: 700, color: T.text1 }}>
                                                                    {child.name}
                                                                </div>
                                                                <div style={{ fontSize: "0.75rem", color: T.text3, marginTop: 1 }}>
                                                                    {child.desc}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <ToggleSwitch
                                                            checked={settings[child.key]}
                                                            disabled={childDisabled}
                                                            onChange={() => handleToggle(child.key)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .ns-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
                .ns-scroll::-webkit-scrollbar { width: 6px; }
                .ns-scroll::-webkit-scrollbar-track { background: transparent; }
                .ns-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .ns-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .ns-nav-item:hover { background: ${T.bg} !important; }
                .ns-panel { transition: box-shadow 0.2s; }
                .ns-link-btn {
                    background: none; border: none; cursor: pointer;
                    font-size: 0.72rem; font-weight: 700;
                    padding: 2px 4px;
                }
                .ns-link-btn:hover { text-decoration: underline; }
                .ns-spin-icon { animation: ns-spin 0.8s linear infinite; }
                @keyframes ns-spin { to { transform: rotate(360deg); } }
                .ns-hero-glow {
                    position: absolute;
                    top: -60px; right: -60px;
                    width: 220px; height: 220px;
                    background: rgba(255,255,255,0.14);
                    border-radius: 50%;
                    pointer-events: none;
                }
                @media (max-width: 760px) {
                    .ns-layout { flex-direction: column; align-items: stretch !important; }
                    .ns-sidebar {
                        width: 100% !important;
                        height: auto !important;
                        max-height: 40vh;
                    }
                    .ns-panel { height: auto !important; }
                }
            `}</style>
        </>
    );
};

export default NotificationSettingsPage;